import { ADMIN_DATE_PLACEHOLDER } from './adminUserDisplay.js';

export const ACTIVITY_DAILY_DAYS = 30;

export const ACTIVITY_KPI_DEFS = Object.freeze([
  {
    id: 'profiles',
    valuePath: ['totals', 'profiles'],
    titleKey: 'admin.activity.kpi.profiles',
    hintKey: 'admin.activity.kpi.profiles.hint',
  },
  {
    id: 'properties',
    valuePath: ['totals', 'properties'],
    titleKey: 'admin.activity.kpi.properties',
    hintKey: null,
  },
  {
    id: 'last24Hours',
    valuePath: ['propertyCreation', 'last24Hours'],
    titleKey: 'admin.activity.kpi.last24Hours',
    hintKey: 'admin.activity.kpi.last24Hours.hint',
  },
  {
    id: 'last7Days',
    valuePath: ['propertyCreation', 'last7Days'],
    titleKey: 'admin.activity.kpi.last7Days',
    hintKey: 'admin.activity.kpi.last7Days.hint',
  },
  {
    id: 'last30Days',
    valuePath: ['propertyCreation', 'last30Days'],
    titleKey: 'admin.activity.kpi.last30Days',
    hintKey: 'admin.activity.kpi.last30Days.hint',
  },
]);

function isUtcDateString(value) {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

/**
 * @param {unknown} value
 * @returns {number | null}
 */
export function readActivityCount(value) {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return null;
  return Math.floor(value);
}

/**
 * @param {object | null | undefined} summary
 * @param {string[]} path
 * @returns {number | null}
 */
export function readActivityPath(summary, path) {
  if (!summary || typeof summary !== 'object' || !Array.isArray(path)) return null;
  let current = summary;
  for (const key of path) {
    if (!current || typeof current !== 'object') return null;
    current = current[key];
  }
  return readActivityCount(current);
}

/**
 * @param {unknown} value
 * @returns {string}
 */
export function formatActivityNumber(value) {
  const n = readActivityCount(value);
  return n == null ? ADMIN_DATE_PLACEHOLDER : String(n);
}

/**
 * @param {unknown} daily
 * @returns {{ date: string, count: number }[]}
 */
export function normalizeActivityDaily(daily) {
  if (!Array.isArray(daily)) return [];
  const rows = [];
  for (const row of daily) {
    if (!row || typeof row !== 'object') continue;
    if (!isUtcDateString(row.date)) continue;
    const count = readActivityCount(row.count);
    rows.push({ date: row.date, count: count == null ? 0 : count });
  }
  return rows;
}

/**
 * @param {{ count: number }[]} daily
 * @returns {boolean}
 */
export function isAllZeroDaily(daily) {
  if (!Array.isArray(daily) || daily.length === 0) return true;
  return daily.every((row) => readActivityCount(row?.count) === 0);
}

/**
 * Chart Y scale is never 0 (avoids NaN bar heights). All-zero series still baseline at 0.
 * @param {number[]} counts
 * @returns {number}
 */
export function activityChartYMax(counts) {
  const nums = Array.isArray(counts) ? counts.map((n) => readActivityCount(n)).filter((n) => n != null) : [];
  const max = nums.length ? Math.max(...nums) : 0;
  return Math.max(1, max);
}

/**
 * Pick first, last, and evenly spaced UTC dates for axis labels.
 * @param {string[]} dates
 * @param {number} [maxLabels]
 * @returns {string[]}
 */
export function selectChartXTicks(dates, maxLabels = 6) {
  if (!Array.isArray(dates) || dates.length === 0) return [];
  const cap = Math.max(2, Math.min(maxLabels, dates.length));
  if (dates.length <= cap) return [...dates];
  const ticks = [];
  for (let i = 0; i < cap; i += 1) {
    const index = Math.round((i * (dates.length - 1)) / (cap - 1));
    const date = dates[index];
    if (date && !ticks.includes(date)) ticks.push(date);
  }
  return ticks;
}

function utcDateFromYmd(date) {
  if (!isUtcDateString(date)) return null;
  const parsed = new Date(`${date}T00:00:00.000Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function localeFor(language) {
  return language === 'en' ? 'en-CA' : 'fr-CA';
}

/**
 * Compact UTC calendar tick: "27 juil." / "Jul 27".
 * @param {string} date
 * @param {string} [language]
 */
export function formatActivityChartTick(date, language = 'fr') {
  const parsed = utcDateFromYmd(date);
  if (!parsed) return ADMIN_DATE_PLACEHOLDER;
  try {
    return new Intl.DateTimeFormat(localeFor(language), {
      day: 'numeric',
      month: 'short',
      timeZone: 'UTC',
    }).format(parsed);
  } catch {
    return date;
  }
}

/**
 * Full UTC calendar day: "24 août 2026" / "August 24, 2026".
 * @param {string} date
 * @param {string} [language]
 */
export function formatActivityChartDate(date, language = 'fr') {
  const parsed = utcDateFromYmd(date);
  if (!parsed) return ADMIN_DATE_PLACEHOLDER;
  try {
    return new Intl.DateTimeFormat(localeFor(language), {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(parsed);
  } catch {
    return date;
  }
}

/**
 * Clock from generatedAt. This is summary computation time, not user activity.
 * @param {unknown} value
 * @param {string} [language]
 */
export function formatActivityClock(value, language = 'fr') {
  if (value == null || value === '') return ADMIN_DATE_PLACEHOLDER;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return ADMIN_DATE_PLACEHOLDER;
  try {
    return new Intl.DateTimeFormat(localeFor(language), {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(date);
  } catch {
    return ADMIN_DATE_PLACEHOLDER;
  }
}

/**
 * French uses singular for 0 and 1. English uses plural except exactly 1.
 * @param {number} count
 * @param {string} [language]
 * @returns {'one' | 'other'}
 */
export function activityCreatedPlural(count, language = 'fr') {
  const n = readActivityCount(count);
  if (n == null) return 'other';
  if (language === 'fr') return n <= 1 ? 'one' : 'other';
  return n === 1 ? 'one' : 'other';
}

/**
 * @param {number} count
 * @param {(key: string) => string} t
 * @param {string} [language]
 */
export function formatActivityCreatedLabel(count, t, language = 'fr') {
  const n = readActivityCount(count);
  if (n == null || typeof t !== 'function') return ADMIN_DATE_PLACEHOLDER;
  const key =
    activityCreatedPlural(n, language) === 'one'
      ? 'admin.activity.created.one'
      : 'admin.activity.created.other';
  return t(key).replaceAll('{count}', String(n));
}

/**
 * @param {string} date
 * @param {number} count
 * @param {(key: string) => string} t
 * @param {string} [language]
 */
export function formatActivityBarLabel(date, count, t, language = 'fr') {
  const day = formatActivityChartDate(date, language);
  const created = formatActivityCreatedLabel(count, t, language);
  return `${day}. ${created}`;
}

/**
 * Integer Y-axis ticks from 0 through yMax inclusive, at most 5 ticks.
 * @param {number} yMax
 * @returns {number[]}
 */
export function activityChartYTicks(yMax) {
  const max = Math.max(1, Math.floor(Number(yMax) || 1));
  if (max <= 4) {
    const ticks = [];
    for (let i = 0; i <= max; i += 1) ticks.push(i);
    return ticks;
  }
  const step = Math.ceil(max / 4);
  const ticks = [0];
  for (let v = step; v < max; v += step) ticks.push(v);
  if (ticks[ticks.length - 1] !== max) ticks.push(max);
  return ticks;
}
